<script setup lang="ts">
import { computed } from "vue";
import { useField } from "vee-validate";
import type { RuleExpression } from "vee-validate";

const props = withDefaults(
  defineProps<{
    id?: string;
    title?: string;
    name?: string;
    rules?: RuleExpression<any>;
    modelValue?: string | number | null;
    readonly?: boolean;
    type?: string;
    required?: boolean;
    min?: number | string;
    max?: number | string;
    placeholder?: string;
  }>(),
  {
    type: "text",
    modelValue: null,
    required: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: string | number | null];
}>();

// Only hook into vee-validate when a name is actually given
const field = props.name ? useField(() => props.name!, props.rules) : null;

const value = computed({
  get: () => (field ? field.value.value : props.modelValue),
  set: (val) => {
    if (field) {
      field.value.value = val;
    } else {
      emit("update:modelValue", val);
    }
  },
});

const errorMessage = computed(() => field?.errorMessage.value);
</script>

<template>
  <div class="flex flex-col gap-1">
    <label :for="id" v-if="title" class="font-semibold text-gray-800 text-sm">{{
      title + "" + (required ? "*" : "")
    }}</label>
    <div
      class="flex flex-row items-center w-full px-3 py-2 border rounded-lg focus-within:shadow-[0_0_0_3px_rgba(31,168,91,0.12)]"
      :class="[
        readonly
          ? 'bg-gray-100! border! border-gray-300! text-gray-500! cursor-not-allowed!'
          : errorMessage
            ? 'border-red-400'
            : 'border-gray-300 focus-within:border-primary',
      ]"
    >
      <slot></slot>
      <input
        class="w-full px-3 focus:outline-none"
        :class="readonly ? 'cursor-not-allowed!' : ''"
        :id="id"
        v-model="value"
        :type="type"
        :readonly="readonly"
        :min="min"
        :max="max"
        :placeholder="placeholder"
      />
    </div>
    <span v-if="errorMessage" class="text-red-500 text-xs">{{
      errorMessage
    }}</span>
  </div>
</template>
